// Pure pieces of the platform drag-to-reorder primitive (port of the admin
// `components/ui/Sortable.utils.ts`, KLUB-264). Kept apart from Sortable.tsx so
// they are unit tested without mounting React or a drag context.

import type { Announcements, ScreenReaderInstructions } from "@dnd-kit/core";

/**
 * Given the current ID order and the active (dragged) + over (drop target) IDs,
 * returns the next order, or `null` when the move is a no-op (same position) or
 * references an unknown ID (defensive — a stale drag against a changed list).
 */
export function computeReorder(
  items: string[],
  activeId: string,
  overId: string,
): string[] | null {
  if (activeId === overId) return null;
  const oldIndex = items.indexOf(activeId);
  const newIndex = items.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1) return null;
  const next = items.slice();
  const [moved] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, moved);
  return next;
}

/**
 * Live-region announcements in pt-BR (dnd-kit defaults are English). They name
 * the POSITION, not coordinates, and the item when `labelOf` knows it.
 */
export function buildAnnouncements(
  items: string[],
  labelOf: (id: string) => string | undefined = () => undefined,
): Announcements {
  const posOf = (id: string | number) => items.indexOf(String(id)) + 1;
  const nameOf = (id: string | number) => {
    const label = labelOf(String(id));
    return label ? `"${label}"` : "Item";
  };
  return {
    onDragStart: ({ active }) =>
      `${nameOf(active.id)} na posição ${posOf(active.id)} de ${items.length} pego. Use as setas para mover.`,
    onDragOver: ({ over }) =>
      over ? `Movendo para a posição ${posOf(over.id)} de ${items.length}.` : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${nameOf(active.id)} solto na posição ${posOf(over.id)} de ${items.length}.`
        : "Reordenação cancelada.",
    onDragCancel: ({ active }) =>
      `Reordenação cancelada. ${nameOf(active.id)} voltou à posição ${posOf(active.id)}.`,
  };
}

/** Operating instructions dnd-kit wires as aria-describedby on each handle. */
export const SCREEN_READER_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    "Para pegar um item, pressione a barra de espaço. Enquanto arrasta, use as setas para mover. Pressione espaço novamente para soltar, ou Esc para cancelar.",
};
